import React, { useLayoutEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/stores/use-auth';
import { useAuthReady } from '@/hooks/useAuthReady';
import { restoreSession } from '@/lib/session';
import {
  parseSafeHostedMcpAuthorizeReturnPath,
  parseSafeRequestAccessReturnPath,
} from '@codemerlin/shared';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Skip redirect to org chooser (MCP consent carries its own org selection). */
  bypassOrganizationChooser?: boolean;
}

function SessionReconnectingFallback(): JSX.Element {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-3">
      <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden />
      <p className="text-sm text-muted-foreground">Reconnecting…</p>
    </div>
  );
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  bypassOrganizationChooser = false,
}) => {
  const { isAuthenticated, user } = useAuth();
  const { isReady, hasResolvedAuth, hadLiveSessionRef } = useAuthReady();
  const location = useLocation();
  const [isReconnecting, setIsReconnecting] = useState(false);
  const prevAuthenticatedRef = useRef(isAuthenticated);

  const authLostOnProtectedRoute =
    hadLiveSessionRef.current &&
    hasResolvedAuth &&
    !isAuthenticated &&
    prevAuthenticatedRef.current;

  useLayoutEffect(() => {
    prevAuthenticatedRef.current = isAuthenticated;
  });

  useLayoutEffect(() => {
    if (!authLostOnProtectedRoute) {
      return;
    }

    let cancelled = false;
    setIsReconnecting(true);

    void restoreSession()
      .catch(() => false)
      .finally(() => {
        if (!cancelled) {
          setIsReconnecting(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [authLostOnProtectedRoute]);

  if (!isReady) {
    return <div className="flex h-screen items-center justify-center">Loading...</div>;
  }

  if (isAuthenticated && !user?.organization && !bypassOrganizationChooser) {
    return <Navigate to="/choose-organization" replace />;
  }

  if (!isAuthenticated) {
    if (authLostOnProtectedRoute || isReconnecting) {
      return <SessionReconnectingFallback />;
    }

    const workItemDetailMatch = location.pathname.match(/^\/work-items\/([0-9a-f-]{36})$/i);
    if (workItemDetailMatch?.[1]) {
      const basePath = `/request-access/work-item/${workItemDetailMatch[1]}`;
      // Keep `?tab=…` when it passes the OAuth return_to allowlist; otherwise drop the query rather
      // than emit a return_to that /signin would discard (which would land the user on `/`).
      const returnTo =
        parseSafeRequestAccessReturnPath(`${basePath}${location.search}`) ??
        parseSafeRequestAccessReturnPath(basePath);
      if (returnTo) {
        const params = new URLSearchParams();
        params.set('flow', 'request_access');
        params.set('return_to', returnTo);
        return <Navigate to={`/signin?${params.toString()}`} replace />;
      }
    }

    const returnCandidate = `${location.pathname}${location.search}`;
    const encodedReturn =
      bypassOrganizationChooser && parseSafeHostedMcpAuthorizeReturnPath(returnCandidate)
        ? `?return_to=${encodeURIComponent(returnCandidate)}`
        : '';
    return <Navigate to={`/signin${encodedReturn}`} state={{ from: location }} replace />;
  }

  return <>{children}</>;
};
